import i18n from '../i18n'
import { faDigits } from './numerals'
import { faVersion } from './version'

/**
 * The download page's rules (`/download`): where the Android build is
 * announced, how its numbers are written, and which of the web app's install
 * paths a browser gets. Pure, so each rule is a unit test.
 *
 * The Android facts are never typed here. `latest.json` is written by
 * `scripts/make_update_manifest.py` at the repo root from the APK it
 * publishes — the same file the app's own update check reads — so this page
 * and the phone agree on what the current build is by construction. The one
 * exception is MIN_ANDROID, which the manifest does not carry.
 */

/** The update channel: nginx's own directory, served outside the staging gate (`/updates/` in nginx.tls.conf.template). */
export const UPDATES_URL = '/updates/'
export const MANIFEST_URL = `${UPDATES_URL}latest.json`

/**
 * The app's page on Café Bazaar. Only the Persian site links it: Bazaar is
 * an Iranian store, and the English site keeps the APK and the web app as
 * its two ways in (owner, 2026-10-16). The Android app itself never links a
 * store (Bazaar's rules forbid external store links in the Bazaar build).
 */
export const BAZAAR_URL = 'https://cafebazaar.ir/app/club.parrots.tooti'

/** Whether a page in [language] offers the Bazaar button: the Persian site only. */
export function bazaarShown(language) {
  return typeof language === 'string' && (language === 'fa' || language.startsWith('fa-'))
}

/**
 * Mirrors `minSdk = 24` in tooti-android/app/build.gradle.kts — Android 7.
 * The install page derives it from the APK with aapt2; a browser cannot, so
 * it is typed once here, next to the file it must follow.
 */
export const MIN_ANDROID = 7

function isUpdateChannelApk(url) {
  try {
    const parsed = new URL(url, 'https://parrots.club')
    return (
      parsed.protocol === 'https:' &&
      parsed.pathname.startsWith(UPDATES_URL) &&
      parsed.pathname.endsWith('.apk')
    )
  } catch {
    return false
  }
}

/**
 * The manifest as this page can show it, or null when it is not one. A shape
 * check rather than trust: the URL must be an APK on the update channel over
 * HTTPS, because a download button pointing anywhere else is the one thing a
 * download page must never render — and then the page falls back to linking
 * the install page, which is always right.
 */
export function parseManifest(data) {
  if (!data || typeof data !== 'object') return null
  const { versionName, versionCode, apkUrl, sha256, notes } = data
  if (typeof versionName !== 'string' || !/^\d+(\.\d+)*$/.test(versionName)) return null
  if (!Number.isInteger(versionCode) || versionCode <= 0) return null
  if (typeof apkUrl !== 'string' || !isUpdateChannelApk(apkUrl)) return null
  if (typeof sha256 !== 'string' || !/^[0-9a-f]{64}$/.test(sha256)) return null
  return {
    versionName,
    versionCode,
    apkUrl,
    sha256,
    notes: typeof notes === 'string' ? notes.trim() : '',
  }
}

/**
 * Decimal megabytes to two places, in Persian: «۲٫۲۶». The same arithmetic
 * as the install page (`size / 1_000_000`, `.2f`), and the number Android's
 * own download list shows next to the file — so a learner comparing the two
 * never has to wonder why they differ. Null when the size is not known.
 */
export function megabytesFa(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return null
  return faDigits((bytes / 1_000_000).toFixed(2)).replace('.', '٫')
}

/** The same number in the page's language: «۲٫۲۶» in fa, "2.26" otherwise. */
export function megabytes(bytes, language = i18n.language) {
  if (language === 'fa') return megabytesFa(bytes)
  if (!Number.isFinite(bytes) || bytes <= 0) return null
  return (bytes / 1_000_000).toFixed(2)
}

const t = (key, options) => i18n.t(key, { ns: 'download', ...options })

/** The chips under the Android card's title: version, size when known, the Android floor — in the page's language. */
export function androidChips({ versionName, sizeBytes = null }) {
  const fa = i18n.language === 'fa'
  const chips = [t('chips.version', { version: faVersion(versionName) })]
  const size = megabytes(sizeBytes)
  if (size) chips.push(t('chips.size', { size }))
  chips.push(t('chips.minAndroid', { version: fa ? faDigits(MIN_ANDROID) : String(MIN_ANDROID) }))
  return chips
}

/**
 * Which of the web app's install paths this browser gets. In order:
 *
 *   'standalone' — already running as the installed app; nothing to install
 *   'installed'  — installed from this page just now (`appinstalled` fired)
 *   'prompt'     — the browser offered `beforeinstallprompt`; a button can show it
 *   'ios'        — Safari on iOS never fires it; the Share sheet is the only way
 *   'manual'     — everything else (desktop Firefox, an early paint before the
 *                  event, a prompt already declined): open in the browser and
 *                  install from its own menu
 *
 * `standalone` outranks `installed`: inside the installed app the fact that
 * it was installed is not news. `prompt` outranks `ios` only in theory —
 * iOS never offers the event — but the order keeps the rule total.
 */
export function webInstallState({ standalone = false, installed = false, canPrompt = false, ios = false }) {
  if (standalone) return 'standalone'
  if (installed) return 'installed'
  if (canPrompt) return 'prompt'
  if (ios) return 'ios'
  return 'manual'
}
